import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { CaptionToken } from "../Data/CaptionToken";
import * as Constants from "../Data/Constants";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { SplashLoadingText } from "../GameObjects/SplashLoadingText";
import { SplashPlayButton } from "../GameObjects/SplashPlayButton";

export class SplashScreen extends HLF.LoadScreen {
    private _bg!: PIXI.Sprite;
    private _loadingText!: SplashLoadingText;
    private _buttonPlay!: SplashPlayButton;
    private _loadCompleteCallback!: () => void;
    private _keyboardFocusManager!: KeyboardFocusManager;

    public constructor() {
        super();
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "splash" + HLF.ResourceUtils.FILE_EXTENSION_JPG,

            Constants.SPRITESHEET_UI + HLF.ResourceUtils.FILE_EXTENSION_JSON,

            Constants.FONT_LUNA + HLF.ResourceUtils.FILE_EXTENSION_FONT,
            Constants.FONT_GEL_MED_35 + HLF.ResourceUtils.FILE_EXTENSION_FONT,

            "andy.spine",
        ];
        return requiredAssets;
    }

    public init(pSize: PIXI.Point) {
        super.init(pSize);

        this._bg = HLF.PixiUtils.makeSprite("splash");
        this._bg.interactive = true;
        this.addChild(this._bg);

        this._loadingText = new SplashLoadingText();
        this._loadingText.position.set(0, 180);
        this.addChild(this._loadingText);

        this._buttonPlay = new SplashPlayButton(this.onButtonPlayPressed.bind(this));
        this._buttonPlay.position.copy(this._loadingText.position);
        this.addChild(this._buttonPlay);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);
    }

    public animateIn(pOnComplete: () => void): void {
        this._loadingText.animateIn(pOnComplete);
        // TODO:SH: Ensure this works across all broswers
        if (Constants.DEBUG_MINIMAL_AUDIO === false) {
            // Disable title VO in Safari, since Howler will play all queued sound effects simultaneously
            // once audio is enabled
            if (HLF.BrowserUtils.isSafari() === false) {
                new TimelineLite().call(() => {
                    PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                        VoiceOver.TITLE, 1, false, HLF.AudioCategory.VO.toString(),
                    ));
                    PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(VoiceOver.TITLE));
                }, undefined, this, 0.25);
            }
            else {
                // Fix for safari audio bug. Preload the sound file to be played, so that Howler's loading delay
                // doesn't prevent Safari from detecting the sound as being a part of an input event
                Application.instance.audio.preload(VoiceOver.PLAY, HLF.AudioCategory.VO);
            }
        }
    }

    public animateOut(pOnComplete: () => void): void {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this, 0.5, {
            alpha: 0, ease: Sine.easeInOut,
        });
        timeline.call(pOnComplete);
    }

    public onLoadComplete(pCallback: () => void): void {
        this._loadCompleteCallback = pCallback;
        this.loadBgm();
    }

    private loadBgm(): void {
        Application.instance.audio.preload("happy-jazz", HLF.AudioCategory.BGM, () => {
            this.onBGMLoaded();
        });
    }

    private onBGMLoaded(): void {
        if (Constants.DEBUG_SKIP_LOAD === false) {
            this._loadingText.animateOut();
            new TimelineLite().add(() => {
                this._buttonPlay.animateIn();
                PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._buttonPlay);
            }, 0.7);
            this._bg.once(HLF.Input.Events.TAP,   this.onButtonPlayPressed.bind(this));
            this._bg.once(HLF.Input.Events.CLICK, this.onButtonPlayPressed.bind(this));
        }
        else {
            this._loadCompleteCallback();
        }
    }

    private onButtonPlayPressed(): void {
        this._buttonPlay.interactive = false;
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        if (Constants.DEBUG_MINIMAL_AUDIO === false) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken("happy-jazz", 1, true, HLF.AudioCategory.BGM));
        }
        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(SoundEffect.UI_SELECT, 1, false, HLF.AudioCategory.SFX));
        // TODO:SH: Do we need to reevaluate load screen cleanup/destroy?
        this._keyboardFocusManager.destroy({ children: true });
        Application.instance.analytics.event("start", "click");
        this._loadCompleteCallback();
    }
}
